import { Check, ChevronDown, Search } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Dialog,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import { cn, filterStaffUsers } from '@/lib/utils';
import {
  useCreateTaskMutation,
  useGetTaskGroupsQuery,
  useGetTaskStatusesQuery,
  useGetTaskTypesQuery,
} from '@/services/api';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import {
  TaskGroup,
  TaskStatus,
  TaskType,
} from '@/services/types/client-intake-types';
import { CoverageSchema } from '@/services/types/coverage-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';
import { User } from '@/services/types/user-types';
import { createConsistentDate } from '@/utils/date';
import { filterTaskStatusesByGroup } from '@/utils/taskStatusUtils';

import { MatterSelectorCombobox } from '../matter/MatterSelectorCombobox';
import { MedicalRecordsValidationDialog } from './MedicalRecordsValidationDialog';
import { TaskConflictDialog } from './TaskConflictDialog';

/* ─── Error guards ─── */

interface TaskConflictError {
  status: number;
  data: {
    error: 'task_conflict';
    message: string;
    existing_task_id: string;
    existing_task_name: string;
    type_name: string;
  };
}

interface TaskValidationError {
  status: number;
  data: {
    error: 'validation_error';
    message: string;
    missing_fields: string[];
  };
}

const isMedicalRecordsValidationError = (
  error: unknown,
): error is TaskValidationError => {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('status' in error) ||
    !('data' in error)
  ) {
    return false;
  }
  const typedError = error as TaskValidationError;
  return (
    typedError.status === 400 &&
    typeof typedError.data === 'object' &&
    typedError.data !== null &&
    typedError.data.error === 'validation_error' &&
    Array.isArray(typedError.data.missing_fields) &&
    typedError.data.missing_fields.includes('treatment_record_request_id')
  );
};

const isTaskConflictError = (error: unknown): error is TaskConflictError => {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('status' in error) ||
    !('data' in error)
  ) {
    return false;
  }
  const typedError = error as TaskConflictError;
  return (
    typedError.status === 409 &&
    typeof typedError.data === 'object' &&
    typedError.data !== null &&
    typedError.data.error === 'task_conflict'
  );
};

/* ─── Shared primitives (Cash App / Uber vibe) ─── */

const FieldLabel = ({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) => (
  <span className="text-[11px] font-bold text-zinc-600 uppercase tracking-wide">
    {children}
    {required && <span className="ml-0.5 text-red-400">*</span>}
  </span>
);

const FieldError = ({ message }: { message: string }) => (
  <p className="text-[11px] font-semibold text-red-500">{message}</p>
);

const FlatInput = ({
  value,
  onChange,
  placeholder,
  error,
  id,
  multiline,
  rows,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  error?: string;
  id?: string;
  multiline?: boolean;
  rows?: number;
}) => {
  const cls = cn(
    'w-full rounded-xl bg-zinc-100 px-3 py-2 text-[13px] font-semibold text-zinc-900 placeholder:text-zinc-500 placeholder:font-semibold focus:outline-none focus:ring-2 focus:ring-zinc-300 transition-shadow',
    error && 'ring-2 ring-red-300',
  );
  if (multiline) {
    return (
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(cls, 'resize-none')}
        rows={rows || 3}
      />
    );
  }
  return (
    <input
      id={id}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cls}
    />
  );
};

const FlatDateInput = ({
  value,
  onChange,
  error,
}: {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  error?: string;
}) => {
  const formatted = value
    ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    : '';
  return (
    <input
      type="date"
      value={formatted}
      onChange={(e) => {
        if (e.target.value) {
          const [year, month, day] = e.target.value.split('-').map(Number);
          onChange(new Date(year, month - 1, day));
        } else {
          onChange(undefined);
        }
      }}
      className={cn(
        'w-full rounded-xl bg-zinc-100 px-3 py-2 text-[13px] font-semibold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300 transition-shadow',
        !formatted && 'text-zinc-500',
        error && 'ring-2 ring-red-300',
      )}
    />
  );
};

/* ─── Flat dropdown (replaces Popover+Command combo) ─── */

interface FlatSelectProps<T extends { id: string; name: string }> {
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  placeholder: string;
  error?: string;
  searchable?: boolean;
}

const FlatSelect = <T extends { id: string; name: string }>({
  options,
  value,
  onChange,
  placeholder,
  error,
  searchable = false,
}: FlatSelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const term = search.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(term));
  }, [options, search]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return () => {};
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center justify-between w-full rounded-xl bg-zinc-100 px-3 py-2 text-[13px] font-semibold text-left transition-shadow focus:outline-none focus:ring-2 focus:ring-zinc-300',
          value ? 'text-zinc-900' : 'text-zinc-500',
          error && 'ring-2 ring-red-300',
        )}
      >
        <span className="truncate">{value?.name || placeholder}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-zinc-500 shrink-0 ml-2 transition-transform duration-150',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full rounded-xl bg-white border border-zinc-200 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150">
          {searchable && (
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-zinc-100">
              <Search className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
              <input
                type="text"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="flex-1 text-[13px] font-semibold text-zinc-900 placeholder:text-zinc-500 placeholder:font-semibold bg-transparent focus:outline-none"
              />
            </div>
          )}
          <div className="max-h-52 overflow-y-auto scrollbar-thin py-0.5">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-[13px] text-zinc-500 font-semibold">
                No results
              </div>
            ) : (
              filtered.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={cn(
                    'flex items-center gap-2 w-full px-3 py-1.5 text-[13px] font-semibold text-left transition-colors',
                    value?.id === option.id
                      ? 'text-zinc-900 bg-zinc-50'
                      : 'text-zinc-700 hover:bg-zinc-50',
                  )}
                >
                  <Check
                    className={cn(
                      'h-3.5 w-3.5 shrink-0',
                      value?.id === option.id
                        ? 'text-zinc-900 opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{option.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Flat user picker ─── */

const FlatUserPicker = ({
  users,
  value,
  onChange,
  isLoading,
  error,
  managingParalegal,
  caseManager,
}: {
  users: User[];
  value: User | null;
  onChange: (user: User | null) => void;
  isLoading?: boolean;
  error?: string;
  managingParalegal?: { id: string } | null;
  caseManager?: { id: string } | null;
}) => {
  // Sort: managing paralegal & case manager first
  const sorted = useMemo(() => {
    const priorityIds = new Set(
      [managingParalegal?.id, caseManager?.id].filter(Boolean) as string[],
    );
    return [...users].sort((a, b) => {
      const aP = priorityIds.has(a.id) ? 0 : 1;
      const bP = priorityIds.has(b.id) ? 0 : 1;
      if (aP !== bP) return aP - bP;
      return (a.first_name || '').localeCompare(b.first_name || '');
    });
  }, [users, managingParalegal, caseManager]);

  const options = useMemo(
    () =>
      sorted.map((u) => ({
        id: u.id,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
      })),
    [sorted],
  );

  return (
    <FlatSelect
      options={options}
      value={
        value
          ? {
              id: value.id,
              name:
                `${value.first_name || ''} ${value.last_name || ''}`.trim() ||
                value.email,
            }
          : null
      }
      onChange={(opt) => {
        if (opt) {
          const user = users.find((u) => u.id === opt.id);
          onChange(user || null);
        } else {
          onChange(null);
        }
      }}
      placeholder={isLoading ? 'Loading...' : 'Select assignee'}
      error={error}
      searchable
    />
  );
};

/* ─── Main component ─── */

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMatterId?: string;
  defaultMatterName?: string;
}

export const CreateTaskModal = ({
  isOpen,
  onClose,
  defaultMatterId,
  defaultMatterName,
}: CreateTaskModalProps) => {
  const { matter_id } = useParams();
  const navigate = useNavigate();
  const [createTask, { isLoading }] = useCreateTaskMutation();

  // Form field state
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [nextActionDate, setNextActionDate] = useState<Date>();
  const [slaResolutionTarget, setSlaResolutionTarget] = useState<Date>();
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(
    defaultMatterId || matter_id || null,
  );
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus | null>(null);
  const [selectedType, setSelectedType] = useState<TaskType | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<TaskGroup | null>(null);
  const [assignee, setAssignee] = useState<User | null>(null);
  const [selectedCoverageId, setSelectedCoverageId] = useState<string | null>(
    null,
  );

  // Validation state
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Conflict dialog state
  const [conflictData, setConflictData] = useState<{
    existingTaskId: string;
    existingTaskName: string;
    typeName: string;
  } | null>(null);
  const [isForceCreating, setIsForceCreating] = useState(false);
  const [showMedicalRecordsError, setShowMedicalRecordsError] = useState(false);

  // API data
  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();
  const { data: taskTypes = [] } = useGetTaskTypesQuery();
  const { data: taskGroups = [] } = useGetTaskGroupsQuery();
  const { data: staffUsers, isLoading: isStaffUsersLoading } =
    useGetStaffUsersQuery();

  const filteredStaffUsers = useMemo(
    () => filterStaffUsers(staffUsers || []),
    [staffUsers],
  );

  const { data: matterDetails } = useGetMatterDetailsQuery(selectedMatterId!, {
    skip: !selectedMatterId,
  });

  const { data: coverages = [], isLoading: isCoveragesLoading } =
    useGetCoveragesQuery(
      { matterId: selectedMatterId! },
      { skip: !selectedMatterId },
    );

  const filteredStatuses = useMemo(() => {
    if (!selectedGroup) return taskStatuses;
    const taskForFiltering = { group: selectedGroup } as Pick<
      import('@/services/types/client-intake-types').Task,
      'group'
    >;
    return filterTaskStatusesByGroup(
      taskStatuses,
      taskForFiltering as import('@/services/types/client-intake-types').Task,
    );
  }, [taskStatuses, selectedGroup]);

  // Validation errors
  const validationErrors = useMemo(() => {
    if (!hasAttemptedSubmit) return {};

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getNextActionDateError = () => {
      if (!nextActionDate) return 'Required';
      const dateOnly = new Date(nextActionDate);
      dateOnly.setHours(0, 0, 0, 0);
      if (dateOnly < today) return 'Cannot be in the past';
      return undefined;
    };

    const getDueDateError = () => {
      if (!slaResolutionTarget) return 'Required';
      const dateOnly = new Date(slaResolutionTarget);
      dateOnly.setHours(0, 0, 0, 0);
      if (dateOnly < today) return 'Cannot be in the past';
      return undefined;
    };

    return {
      name: !name ? 'Required' : undefined,
      type: !selectedType ? 'Required' : undefined,
      group: !selectedGroup ? 'Required' : undefined,
      nextActionDate: getNextActionDateError(),
      slaResolutionTarget: getDueDateError(),
      assignee: !assignee ? 'Required' : undefined,
    };
  }, [
    hasAttemptedSubmit,
    name,
    selectedType,
    selectedGroup,
    nextActionDate,
    slaResolutionTarget,
    assignee,
  ]);

  const isFormValid = useMemo(() => {
    if (
      !selectedMatterId ||
      !name ||
      !selectedType ||
      !selectedStatus ||
      !selectedGroup ||
      !nextActionDate ||
      !slaResolutionTarget ||
      !assignee
    ) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const nextActionDateOnly = new Date(nextActionDate);
    nextActionDateOnly.setHours(0, 0, 0, 0);

    const dueDateOnly = new Date(slaResolutionTarget);
    dueDateOnly.setHours(0, 0, 0, 0);

    return nextActionDateOnly >= today && dueDateOnly >= today;
  }, [
    selectedMatterId,
    name,
    selectedType,
    selectedStatus,
    selectedGroup,
    nextActionDate,
    slaResolutionTarget,
    assignee,
  ]);

  // Set defaults
  useEffect(() => {
    if (taskStatuses.length > 0 && !selectedStatus) {
      const pendingStatus = taskStatuses.find(
        (status) => status.canonical_status === CanonicalTaskStatus.PENDING,
      );
      setSelectedStatus(pendingStatus || taskStatuses[0]);
    }
  }, [taskStatuses, selectedStatus]);

  useEffect(() => {
    if (selectedStatus && selectedGroup) {
      const isStatusValid = filteredStatuses.some(
        (s) => s.id === selectedStatus.id,
      );
      if (!isStatusValid && filteredStatuses.length > 0) {
        const pendingStatus = filteredStatuses.find(
          (status) => status.canonical_status === CanonicalTaskStatus.PENDING,
        );
        setSelectedStatus(pendingStatus || filteredStatuses[0]);
      }
    }
  }, [selectedGroup, filteredStatuses, selectedStatus]);

  useEffect(() => {
    if (taskTypes.length > 0 && !selectedType) {
      const otherType = taskTypes.find(
        (type) => type.canonical_type === 'Other',
      );
      setSelectedType(otherType || taskTypes[0]);
    }
  }, [taskTypes, selectedType]);

  useEffect(() => {
    if (taskGroups.length > 0 && !selectedGroup) {
      const miscGroup = taskGroups.find(
        (group) => group.canonical_group === CanonicalTaskGroup.MISCELLANEOUS,
      );
      setSelectedGroup(miscGroup || taskGroups[0]);
    }
  }, [taskGroups, selectedGroup]);

  useEffect(() => {
    setSelectedCoverageId(null);
  }, [selectedMatterId]);

  useEffect(() => {
    if (
      selectedGroup &&
      ![
        CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
        CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
        CanonicalTaskGroup.ADDITIONAL_INSURANCE,
      ].includes(selectedGroup.canonical_group)
    ) {
      setSelectedCoverageId(null);
    }
  }, [selectedGroup]);

  const formatCoverageLabel = (coverage: CoverageSchema) => {
    const carrierName = coverage.carrier?.name?.trim() || 'Unknown Carrier';
    const category =
      coverage.category
        ?.replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase()) || 'Coverage';
    const policyIdentifier =
      coverage.policy_identifiers && coverage.policy_identifiers.length > 0
        ? coverage.policy_identifiers[0].identifier_value
        : null;

    return [carrierName, category, policyIdentifier]
      .filter(Boolean)
      .join(' \u2022 ');
  };

  const resetForm = useCallback(() => {
    setName('');
    setNotes('');
    setNextActionDate(undefined);
    setSlaResolutionTarget(undefined);
    setSelectedStatus(null);
    setSelectedMatterId(defaultMatterId || matter_id || null);
    setSelectedType(null);
    setSelectedGroup(null);
    setAssignee(null);
    setSelectedCoverageId(null);
    setHasAttemptedSubmit(false);
    setConflictData(null);
    setIsForceCreating(false);
    setShowMedicalRecordsError(false);
  }, [defaultMatterId, matter_id]);

  const performCreateTask = useCallback(
    async (forceCreate = false) => {
      try {
        const task = await createTask({
          matterId: selectedMatterId!,
          task: {
            name,
            description: notes || undefined,
            due_date: createConsistentDate(nextActionDate!),
            status_id: selectedStatus!.id,
            type_id: selectedType!.id,
            group_id: selectedGroup!.id,
            assignee_id: assignee!.id,
            sla_resolution_target: createConsistentDate(slaResolutionTarget!),
            coverage_id: selectedCoverageId,
          },
          forceCreate,
        }).unwrap();

        navigate(
          matter_id
            ? {
                pathname: `/matters/${matter_id}/`,
                search: `?task_id=${task.id}`,
              }
            : {
                pathname: '/tasks',
                search: `?task=${task.id}&task_matter=${selectedMatterId}`,
              },
        );
        resetForm();
        onClose();
      } catch (error) {
        if (isTaskConflictError(error)) {
          setConflictData({
            existingTaskId: error.data.existing_task_id,
            existingTaskName: error.data.existing_task_name,
            typeName: error.data.type_name,
          });
          return;
        }
        if (isMedicalRecordsValidationError(error)) {
          setShowMedicalRecordsError(true);
          return;
        }
        console.error('Failed to create task:', error);
        toast.error('Failed to create task. Please try again.');
      }
    },
    [
      createTask,
      selectedMatterId,
      name,
      notes,
      nextActionDate,
      selectedStatus,
      selectedType,
      selectedGroup,
      assignee,
      slaResolutionTarget,
      selectedCoverageId,
      matter_id,
      navigate,
      resetForm,
      onClose,
    ],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    if (!isFormValid) return;
    await performCreateTask(false);
  };

  const handleViewExistingTask = useCallback(() => {
    if (!conflictData) return;
    navigate(
      matter_id
        ? {
            pathname: `/matters/${matter_id}/`,
            search: `?task_id=${conflictData.existingTaskId}`,
          }
        : {
            pathname: '/tasks',
            search: `?task=${conflictData.existingTaskId}&task_matter=${selectedMatterId}`,
          },
    );
    setConflictData(null);
    resetForm();
    onClose();
  }, [conflictData, matter_id, selectedMatterId, navigate, resetForm, onClose]);

  const handleCreateAnyway = useCallback(async () => {
    setIsForceCreating(true);
    try {
      await performCreateTask(true);
    } finally {
      setIsForceCreating(false);
    }
  }, [performCreateTask]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          resetForm();
          onClose();
        }
      }}
    >
      <RightDialogContent className="flex flex-col gap-0 overflow-auto bg-white sm:max-w-[480px] !shadow-none !border-zinc-200">
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <DialogTitle className="text-base font-bold text-zinc-900">
            New Task
          </DialogTitle>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5 px-5 pb-5">
          {/* Matter */}
          {defaultMatterId ? (
            <div className="flex flex-col gap-1">
              <FieldLabel required>Matter</FieldLabel>
              <div className="rounded-xl bg-zinc-100 px-3 py-2 text-[13px] font-semibold text-zinc-900">
                {defaultMatterName || selectedMatterId}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <FieldLabel required>Matter</FieldLabel>
              <MatterSelectorCombobox
                placeholder="Select matter"
                value={selectedMatterId || ''}
                onChange={(id) => setSelectedMatterId(id || null)}
                includeAllOption={false}
                aria-label="Select matter for task"
              />
            </div>
          )}

          {/* Task Type */}
          <div className="flex flex-col gap-1">
            <FieldLabel required>Task Type</FieldLabel>
            <FlatSelect
              placeholder="Select task type"
              options={taskTypes}
              value={selectedType}
              error={validationErrors.type}
              searchable
              onChange={(value) => {
                if (value) {
                  setSelectedType(value);
                  if (value.canonical_type !== CanonicalTaskType.OTHER) {
                    setName(value.name || '');
                  }
                  const associatedGroup = taskGroups.find(
                    (group) => group.canonical_group === value.canonical_group,
                  );
                  if (associatedGroup) {
                    setSelectedGroup(associatedGroup);
                  }
                }
              }}
            />
            {validationErrors.type && (
              <FieldError message={validationErrors.type} />
            )}
          </div>

          {/* Task Group (only for Other type) */}
          {selectedType?.canonical_type === CanonicalTaskType.OTHER && (
            <div className="flex flex-col gap-1">
              <FieldLabel required>Task Group</FieldLabel>
              <FlatSelect
                placeholder="Select task group"
                options={taskGroups}
                value={selectedGroup}
                error={validationErrors.group}
                searchable
                onChange={(value) => {
                  if (value) setSelectedGroup(value);
                }}
              />
              {validationErrors.group && (
                <FieldError message={validationErrors.group} />
              )}
            </div>
          )}

          {/* Task Name */}
          <div className="flex flex-col gap-1">
            <FieldLabel required>Task Name</FieldLabel>
            <FlatInput
              id="name"
              value={name}
              onChange={setName}
              placeholder="What needs to be done?"
              error={validationErrors.name}
            />
            {validationErrors.name && (
              <FieldError message={validationErrors.name} />
            )}
          </div>

          {/* Notes */}
          <div className="flex flex-col gap-1">
            <FieldLabel>Notes</FieldLabel>
            <FlatInput
              id="notes"
              value={notes}
              onChange={setNotes}
              placeholder="Add a description..."
              multiline
              rows={3}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-2.5">
            <div className="flex flex-col gap-1">
              <FieldLabel required>Next Action</FieldLabel>
              <FlatDateInput
                value={nextActionDate}
                onChange={setNextActionDate}
                error={validationErrors.nextActionDate}
              />
              {validationErrors.nextActionDate && (
                <FieldError message={validationErrors.nextActionDate} />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel required>Due Date</FieldLabel>
              <FlatDateInput
                value={slaResolutionTarget}
                onChange={setSlaResolutionTarget}
                error={validationErrors.slaResolutionTarget}
              />
              {validationErrors.slaResolutionTarget && (
                <FieldError message={validationErrors.slaResolutionTarget} />
              )}
            </div>
          </div>

          {/* Status + Assignee */}
          <div className="grid grid-cols-2 gap-2.5">
            <div className="flex flex-col gap-1">
              <FieldLabel>Status</FieldLabel>
              <FlatSelect
                placeholder="Select status"
                options={filteredStatuses}
                value={selectedStatus}
                onChange={(value) => {
                  if (value) setSelectedStatus(value);
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel required>Assignee</FieldLabel>
              <FlatUserPicker
                users={filteredStaffUsers || []}
                value={assignee}
                onChange={setAssignee}
                isLoading={isStaffUsersLoading}
                error={validationErrors.assignee}
                managingParalegal={matterDetails?.firm?.managing_paralegal}
                caseManager={matterDetails?.case_manager}
              />
              {validationErrors.assignee && (
                <FieldError message={validationErrors.assignee} />
              )}
            </div>
          </div>

          {/* Coverage selector */}
          {selectedMatterId &&
            selectedGroup &&
            [
              CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
              CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
              CanonicalTaskGroup.ADDITIONAL_INSURANCE,
            ].includes(selectedGroup.canonical_group) && (
              <div className="flex flex-col gap-1">
                <FieldLabel>Link to Coverage</FieldLabel>
                {isCoveragesLoading ? (
                  <div className="rounded-xl bg-zinc-100 px-3 py-2 text-[13px] font-semibold text-zinc-500">
                    Loading coverages...
                  </div>
                ) : coverages.length === 0 ? (
                  <div className="rounded-xl bg-zinc-100 px-3 py-2 text-[13px] font-semibold text-zinc-500">
                    No coverages available
                  </div>
                ) : (
                  <FlatSelect
                    placeholder="Select coverage (optional)"
                    options={[
                      { id: '', name: 'No coverage linked' },
                      ...coverages.map((coverage) => ({
                        id: coverage.id,
                        name: formatCoverageLabel(coverage),
                      })),
                    ]}
                    value={
                      selectedCoverageId
                        ? {
                            id: selectedCoverageId,
                            name: coverages.find(
                              (c) => c.id === selectedCoverageId,
                            )
                              ? formatCoverageLabel(
                                  coverages.find(
                                    (c) => c.id === selectedCoverageId,
                                  )!,
                                )
                              : 'Selected coverage',
                          }
                        : null
                    }
                    onChange={(value) => {
                      setSelectedCoverageId(value?.id || null);
                    }}
                    searchable
                  />
                )}
              </div>
            )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-9 rounded-xl bg-zinc-100 text-[13px] font-bold text-zinc-700 hover:bg-zinc-200 active:scale-[0.98] transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 h-9 rounded-xl bg-zinc-900 text-[13px] font-bold text-white hover:bg-zinc-800 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </RightDialogContent>

      <TaskConflictDialog
        isOpen={conflictData !== null}
        onClose={() => setConflictData(null)}
        onViewExisting={handleViewExistingTask}
        onCreateAnyway={handleCreateAnyway}
        existingTaskName={conflictData?.existingTaskName ?? ''}
        typeName={conflictData?.typeName ?? ''}
        isCreating={isForceCreating}
      />

      <MedicalRecordsValidationDialog
        isOpen={showMedicalRecordsError}
        onClose={() => setShowMedicalRecordsError(false)}
      />
    </Dialog>
  );
};

/* ─── Backwards-compatible export for other consumers ─── */

interface ComboboxProps<T extends { id: string; name: string }> {
  label?: string;
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  placeholder: string;
  required?: boolean;
  error?: string;
}

export const Combobox = <T extends { id: string; name: string }>({
  label,
  options,
  value,
  onChange,
  placeholder,
  required,
  error,
}: ComboboxProps<T>) => (
  <div className="flex flex-col gap-1">
    {label && (
      <FieldLabel required={required}>{label}</FieldLabel>
    )}
    <FlatSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      error={error}
      searchable
    />
    {error && <FieldError message={error} />}
  </div>
);
